import { useKeycloak } from '@react-keycloak/web';
import { create } from 'zustand'
import { getProjectById, getProjects } from '../api/projects';
import { getProjectDailyUpdates } from '../api/dailyUpdates';
import { getProjectActionItems } from '../api/actionItems';
import { getProjectMeetingNotes } from '../api/meetingNotes';

/**
 * Creates a store for projects.
 * @returns {ProjectsStore} The store.
 */
export const useProjectsStore = create<ProjectsStore>((set) => {
  return {
    projects: [],
    fullProjects: [],
    pagination: {
      page: 0,
      size: 0,
      total: 0
    },
    setProjectsPage: (projectsPageData: ProjectsInterface) => {
      set((_state) => ({
        pagination: {
          page: projectsPageData.page,
          size: projectsPageData.size,
          total: projectsPageData.total
        },
        projects: [
          ...projectsPageData.results
        ]
      }))
    },
    setProjectById: (project: ProjectInterface) => {
      set((state) => {
        return {
          fullProjects: [
            ...state.fullProjects.filter((p) => p.id !== project.id),
            project
          ]
        }
      })
    }
  };
});

// @ts-ignore
export const useProjectDailyUpdateStore = create<ProjectDailyUpdateStore>((set) => {
  return {
    projectDailyUpdates: {},
    pagination: {
      page: 0,
      size: 0,
      total: 0
    },
    setProjectDailyUpdatePage: (project_id: string, projectDailyUpdatePageData: ProjectDailyUpdatesInterface) => {
      set((state): any => ({
          pagination: {
            page: projectDailyUpdatePageData.page,
            size: projectDailyUpdatePageData.size,
            total: projectDailyUpdatePageData.total
          },
          projectDailyUpdates: {
            ...state.projectDailyUpdates,
            [project_id]: projectDailyUpdatePageData.results
          }
        })
      )
    },
  };
});

export const useProjectActionItemsStore = create<ProjectActionItemsStore>((set) => {
  return {
    projectActionItems: {},
    pagination: {
      page: 0,
      size: 0,
      total: 0
    },
    setProjectActionItemsPage: (project_id: string, projectActionItemsPageData: ProjectActionItemsInterface) => {
      set((state): any => ({
          pagination: {
            page: projectActionItemsPageData.page,
            size: projectActionItemsPageData.size,
            total: projectActionItemsPageData.total
          },
          projectActionItems: {
            ...state.projectActionItems,
            [project_id]: projectActionItemsPageData.results
          }
        })
      )
    },
  };
});

// @ts-ignore
export const useProjectMeetingNotesStore = create<ProjectMeetingNotesStore>((set) => {
  return {
    projectMeetingNotes: {},
    pagination: {
      page: 0,
      size: 0,
      total: 0
    },
    setProjectMeetingNotesPage: (project_id: string, projectMeetingNotesPageData: ProjectMeetingNotesInterface) => {
      set((state): any => ({
          pagination: {
            page: projectMeetingNotesPageData.page,
            size: projectMeetingNotesPageData.size,
            total: projectMeetingNotesPageData.total
          },
          projectMeetingNotes: {
            ...state.projectMeetingNotes,
            [project_id]: projectMeetingNotesPageData.results
          }
        })
      )
    },
  };
});

export const useProjectsPage = () => {
  const {keycloak: { token }} = useKeycloak();

  return {
    loadProjectsPage: async (_page: number) => {
      if (!token) return;

      const setProjectsPage = useProjectsStore.getState().setProjectsPage;
      setProjectsPage(await getProjects(token));
    },

    loadProjectById: async (id: string) => {
      if (!token) return;

      try {
        console.log('🔄 Loading project by ID:', id);
        
        // Load project data with sites included (thanks to include_sites=true parameter)
        const projectData = await getProjectById(token, id);
        console.log('📦 Project data loaded with sites:', projectData);
        
        // Sites are already included in projectData.sites due to include_sites=true parameter
        // No need for separate API call - just use the project data as is
        console.log('✅ Sites included in main response:', projectData.sites?.length || 0, 'sites');
        
        const setProjectById = useProjectsStore.getState().setProjectById;
        setProjectById(projectData);
      } catch (error: any) {
        console.error('❌ Error loading project:', error);
        
        // Handle 404 errors specifically
        if (error.message && error.message.includes('not found')) {
          console.error('🔍 Project not found, redirecting to 404');
          // You can add navigation to 404 page here if needed
          // navigate('/404');
        }
        
        throw error;
      }
    }
  }
}

export const useProjectDailyUpdatePage = (project_id: string) => {
  const { keycloak: { token }} = useKeycloak();

  return {
    loadProjectDailyUpdatesPage: async (_page: number) => {
      if (!token) return;

      const setProjectDailyUpdatePage = useProjectDailyUpdateStore.getState().setProjectDailyUpdatePage;
      setProjectDailyUpdatePage(project_id, await getProjectDailyUpdates(token, project_id));
    },
  }
}

export const useProjectActionItemsPage = (project_id: string) => {
  const { keycloak: { token }} = useKeycloak();

  return {
    loadProjectActionItemsPage: async (_page: number) => {
      if (!token) return;

      const setProjectActionItemsPage = useProjectActionItemsStore.getState().setProjectActionItemsPage;
      setProjectActionItemsPage(project_id, await getProjectActionItems(token, project_id));
    },
  }
}

export const useProjectMeetingNotesPage = (project_id: string) => {
  const { keycloak: { token }} = useKeycloak();

  return {
    loadProjectMeetingNotesPage: async (_page: number) => {
      if (!token) return;

      const setProjectMeetingNotesPage = useProjectMeetingNotesStore.getState().setProjectMeetingNotesPage;
      setProjectMeetingNotesPage(project_id, await getProjectMeetingNotes(token, project_id));
    },
  }
}
